import { useMediaQuery } from '@material-ui/core'
import { Disclaimer } from 'opensolar-ui'
import useExistingSystem from 'projectSections/sections/energy/Retrofit/useExistingSystem'
import { useTranslate } from 'ra-core'
import { SystemComponentBlock, SystemComponentContainer } from './SystemComponent'

export const populateGeneric = (templateString, data) => {
  for (var key in data) {
    var regex = new RegExp('\\{\\{\\s?' + key + '\\s?\\}\\}', 'g')
    if (regex.test(templateString)) {
      templateString = templateString.replace(regex, data[key])
    }

    // Also replace the variation with {{system.key}} in addition to {{key}}
    // @TODO: Remove the need for this inefficient hack, probably standardize on {{system.key}}
    regex = new RegExp('\\{\\{\\s?system.' + key + '\\s?\\}\\}', 'g')
    if (regex.test(templateString)) {
      templateString = templateString.replace(regex, data[key])
    }
  }

  return templateString
}

const getIsFeatured = (componentData) =>
  !!(
    componentData &&
    componentData.component_content &&
    componentData.component_content.is_featured &&
    componentData.component_content.description_featured &&
    componentData.component_content.description_featured.length
  )

const getImageUrl = (system, type) => {
  try {
    return system.component_content[`${type}_image_url`]
  } catch (error) {
    return null
  }
}

export const getContentTemplate = (componentData) => {
  const content =
    componentData?.component_content?.description_featured || componentData?.component_content?.description_standard
  return content || null
}

const getVideoUrl = (component) => {
  try {
    return component.component_content.video_url
  } catch (error) {
    return null
  }
}

const getProductDetails = (component) => {
  try {
    const isProductDetailsPDF = component.component_content?.product_details_is_pdf
    if (isProductDetailsPDF) return component.component_content?.product_details_pdf_url
    return component.component_content.product_details
  } catch (error) {
    return null
  }
}

const getSpecSheetUrl = (component) => {
  try {
    const isSpecSheetPDF = component.component_content.spec_sheet_is_pdf
    if (isSpecSheetPDF) return component.component_content.spec_sheet_pdf_url
    return component.component_content.spec_sheet_url
  } catch (error) {
    return null
  }
}

export const systemComponentsGeneralContent = (titleDefault, component, content) => {
  const isFeatured = getIsFeatured(component)
  return {
    title: component?.component_content?.title || titleDefault,
    componentCode: component?.code ?? '',
    componentManufacturer: component?.manufacturer_name ?? '',
    isFeatured: isFeatured,
    content: component ? content : null,
    productImageUrl: getImageUrl(component, 'promotional'),
    logoUrl: getImageUrl(component, 'logo'),
    videoUrl: getVideoUrl(component),
    productDetails: getProductDetails(component),
    specSheetUrl: getSpecSheetUrl(component),
  }
}

const getComponentDetails = (selectedSystem, componentType) => {
  const keyMapping = {
    module: {
      details: 'module_codes_with_details',
      type: 'moduleTypes',
    },
    inverter: {
      details: 'inverter_codes_with_details',
      type: 'inverters',
    },
    battery: {
      details: 'battery_codes_with_details',
      type: 'batteries',
    },
    other: {
      details: 'other_codes_with_details',
      type: 'others',
    },
  }

  const results = []

  const detailsData = selectedSystem[keyMapping[componentType].details]
  const componentsArray = selectedSystem[keyMapping[componentType].type]

  if (!detailsData || !componentsArray) {
    return results
  }

  for (const [code, details] of Object.entries(detailsData)) {
    for (const component of componentsArray) {
      // We use !!component.show_customer and !!component.show_in_your_solution as a hack because
      // it looks like these values may sometimes be empty-string instead of false
      let show_customer = component.hasOwnProperty('show_customer') ? !!component.show_customer : true
      let show_in_your_solution = component.hasOwnProperty('show_in_your_solution')
        ? !!component.show_in_your_solution
        : true

      if (component.code === code && show_customer && show_in_your_solution) {
        results.push({ ...details, component })
        break
      }
    }
  }
  return results
}
const allSystemComponentPlaceholders = (selectedSystem) => {
  return {
    systemKwStc: selectedSystem.systemKwStc,
    moduleCode: selectedSystem.moduleCode,
    moduleQuantity: selectedSystem.moduleQuantity,
    moduleEfficiency: selectedSystem.moduleEfficiency,
    moduleManufacturer: selectedSystem.moduleManufacturer,
    moduleWattStc: selectedSystem.moduleWattStc,
    moduleProductWarranty: selectedSystem.moduleProductWarranty,
    modulePerformanceWarranty: selectedSystem.modulePerformanceWarranty,
    systemOutputAnnualkWh: selectedSystem.systemOutputAnnualkWh,
    systemLifetime: selectedSystem.systemLifetime,
    systemTotalGeneration: selectedSystem.systemTotalGeneration,
    inverterTotalRatingKw: selectedSystem.inverterTotalRatingKw,
    inverterManufacturer: selectedSystem.inverterManufacturer,
    inverterQuantity: selectedSystem.inverterQuantity,
    inverterCodes: selectedSystem.inverterCodes,
    inverterProductWarranty: selectedSystem.inverterProductWarranty,
    inverterAdditionalPartsWarranty: selectedSystem.inverterAdditionalPartsWarranty,
    inverterNominalAcPower: selectedSystem.inverterNominalAcPower,
    batteryManufacturer: selectedSystem.batteryManufacturer,
    batteryCodes: selectedSystem.batteryCodes,
    batteryProductWarranty: selectedSystem.batteryProductWarranty,
    batteryThroughput: selectedSystem.batteryThroughput,
    batteryCycleLife: selectedSystem.batteryCycleLife,
    // These are component-scoped battery placeholders, so keep them blank in the
    // shared base map and only populate them in the battery branch below.
    batteryKwhOptimal: '',
    batteryKwhUsable: '',
    systemBatteryKwhOptimal: selectedSystem.systemBatteryKwhOptimal,
    systemBatteryUsableKwh: selectedSystem.systemBatteryUsableKwh,
    otherComponentCodes: selectedSystem.otherComponentCodes,
    otherManufacturer: selectedSystem.otherManufacturer,
    otherTitle: selectedSystem.otherTitle,
    otherDescription: selectedSystem.otherDescription,
    otherProductWarranty: selectedSystem.otherProductWarranty,
    quantity: selectedSystem.quantity,
  }
}
const moduleContent = (moduleDetail, selectedSystem) => {
  const templateString = getContentTemplate(moduleDetail.component)
  const data = {
    ...allSystemComponentPlaceholders(selectedSystem),
    // Use module-specific data from moduleDetail
    moduleManufacturer: moduleDetail.manufacturer_name || '',
    moduleCode: moduleDetail.module_code || '',
    moduleWattStc: moduleDetail.kw_stc_per_module ? String(Math.round(moduleDetail.kw_stc_per_module * 1000)) : '',
    moduleQuantity: String(moduleDetail.quantity || 0),
    moduleEfficiency: moduleDetail.efficiency || '',
    moduleProductWarranty: moduleDetail.moduleProductWarranty || '',
    modulePerformanceWarranty: moduleDetail.modulePerformanceWarranty || '',
  }
  return populateGeneric(templateString, data)
}

// Keep the existing ModuleContent function for legacy systems
const ModuleContent = (selectedSystem) => {
  const templateString = getContentTemplate(selectedSystem.module)
  const data = {
    ...allSystemComponentPlaceholders(selectedSystem),
    moduleManufacturer: selectedSystem.moduleManufacturer || '',
    systemKwStc: selectedSystem.systemKwStc || '',
    moduleQuantity: selectedSystem.moduleQuantity || '',
    moduleWattStc: selectedSystem.moduleWattStc || '',
    moduleCode: selectedSystem.moduleCode || '',
    systemOutputAnnualkWh: selectedSystem.systemOutputAnnualkWh || '',
    moduleProductWarranty: selectedSystem.moduleProductWarranty || '',
    modulePerformanceWarranty: selectedSystem.modulePerformanceWarranty || '',
    moduleEfficiency: selectedSystem.moduleEfficiency || '',
  }
  return populateGeneric(templateString, data)
}

// Fix the getModuleBlocks function to use the correct function for each path
const getModuleBlocks = (selectedSystem, layout, showDialog, proposalData) => {
  // Check if we have multiple module types
  if (
    selectedSystem.module_codes_with_details &&
    Object.keys(selectedSystem.module_codes_with_details).length > 0 &&
    selectedSystem.moduleTypes
  ) {
    // New multiple module types approach
    const moduleDetails = getComponentDetails(selectedSystem, 'module')

    if (moduleDetails.length === 0) {
      return null
    }

    return moduleDetails.map((moduleDetail, index) => (
      <SystemComponentBlock
        key={`module_${index}`}
        component={systemComponentsGeneralContent(
          'Solar Panels',
          moduleDetail.component,
          moduleContent(moduleDetail, selectedSystem)
        )}
        isMobile={layout === 'small'}
        showDialog={showDialog}
        proposalData={proposalData}
        allComponentData={moduleDetail.component}
      />
    ))
  } else if (selectedSystem.module && parseInt(selectedSystem.moduleQuantity || 0) > 0) {
    // Legacy single module approach
    return (
      <SystemComponentBlock
        component={systemComponentsGeneralContent('Solar Panels', selectedSystem.module, ModuleContent(selectedSystem))}
        isMobile={layout === 'small'}
        showDialog={showDialog}
        proposalData={proposalData}
        allComponentData={selectedSystem.module}
      />
    )
  } else {
    // No modules to display
    return null
  }
}

const inverterContent = (inverterDetails, selectedSystem) => {
  // Beware we are no longer grouping inverters by type!
  const inverterTotalRatingKw = window.roundToDecimalPlaces(
    inverterDetails.max_power_rating * inverterDetails.quantity,
    3
  )
  const inverterCodes = `${inverterDetails.quantity} x ${inverterDetails.code}`
  const inverterManufacturer = inverterDetails.manufacturer_name

  const templateString = getContentTemplate(inverterDetails.component)
  const data = {
    ...allSystemComponentPlaceholders(selectedSystem),
    inverterTotalRatingKw,
    inverterCodes,
    inverterManufacturer,
    inverterProductWarranty: selectedSystem.inverterProductWarranty,
    inverterAdditionalPartsWarranty: selectedSystem.inverterAdditionalPartsWarranty,
    moduleQuantity: selectedSystem.moduleQuantity,
  }
  return populateGeneric(templateString, data)
}

export const getInverterBlocks = (selectedSystem, layout, showDialog, proposalData) => {
  const invertersDetails = getComponentDetails(selectedSystem, 'inverter')
  const results = invertersDetails.map((inverterDetails, index) => {
    const defaultTitle = inverterDetails.component.microinverter ? 'Microinverter' : 'Inverter'
    return (
      <SystemComponentBlock
        key={`inverter_${index}`}
        component={systemComponentsGeneralContent(
          defaultTitle,
          inverterDetails.component,
          inverterContent(inverterDetails, selectedSystem)
        )}
        isMobile={layout === 'small'}
        showDialog={showDialog}
        proposalData={proposalData}
        allComponentData={inverterDetails.component}
      />
    )
  })
  return results
}

const batteryContent = (batteryDetails, selectedSystem) => {
  // Beware we are no longer grouping batteries by type!
  const batteryKwhOptimal = window.roundToDecimalPlaces(batteryDetails.kwh_optimal * batteryDetails.quantity, 3)
  const batteryUsableCapacity =
    batteryDetails.usable_capacity ?? (batteryDetails.kwh_optimal || 0) * (batteryDetails.depth_of_discharge_factor || 0)
  const batteryKwhUsable = window.roundToDecimalPlaces(batteryUsableCapacity * batteryDetails.quantity, 3)
  const batteryCodes = `${batteryDetails.quantity} x ${batteryDetails.code}`
  const batteryManufacturer = batteryDetails.manufacturer_name

  const templateString = getContentTemplate(batteryDetails.component)
  const data = {
    ...allSystemComponentPlaceholders(selectedSystem),
    batteryKwhOptimal,
    batteryKwhUsable,
    batteryCodes,
    batteryManufacturer,
    batteryProductWarranty: selectedSystem.batteryProductWarranty,
    moduleQuantity: selectedSystem.moduleQuantity,
  }
  return populateGeneric(templateString, data)
}

const getBatteryBlocks = (selectedSystem, layout, showDialog, proposalData) => {
  const batteryDetails = getComponentDetails(selectedSystem, 'battery')
  const results = batteryDetails.map((batteryDetails, index) => {
    return (
      <SystemComponentBlock
        key={`battery_${index}`}
        component={systemComponentsGeneralContent(
          'Battery',
          batteryDetails.component,
          batteryContent(batteryDetails, selectedSystem)
        )}
        isMobile={layout === 'small'}
        showDialog={showDialog}
        proposalData={proposalData}
        allComponentData={batteryDetails.component}
      />
    )
  })
  return results
}

const otherContent = (otherDetails, selectedSystem) => {
  // otherDetails has both details (from other_codes_with_details) and component (from others array)
  // Prefer details fields, fallback to component fields
  const code = otherDetails.code ?? otherDetails.component?.code ?? ''
  const quantity = otherDetails.quantity ?? otherDetails.component?.quantity ?? 0
  const description = otherDetails.description ?? otherDetails.component?.description ?? ''
  const manufacturer_name = otherDetails.manufacturer_name ?? otherDetails.component?.manufacturer_name ?? ''
  const title = otherDetails.title ?? otherDetails.component?.title ?? ''
  const product_warranty = otherDetails.product_warranty ?? otherDetails.component?.product_warranty

  const otherComponentCodes = `${quantity} x ${code}`

  const templateString = getContentTemplate(otherDetails.component)
  const data = {
    ...allSystemComponentPlaceholders(selectedSystem),
    description,
    otherComponentCodes,
    // Component-specific other component fields
    otherManufacturer: manufacturer_name,
    otherTitle: title,
    otherDescription: description,
    otherProductWarranty: product_warranty != null ? String(product_warranty) : '',
    moduleQuantity: selectedSystem.moduleQuantity,
  }
  return populateGeneric(templateString, data)
}

const getOtherBlocks = (selectedSystem, layout, showDialog, proposalData) => {
  const otherDetails = getComponentDetails(selectedSystem, 'other')
  const results = otherDetails.map((otherDetails, index) => {
    return (
      <SystemComponentBlock
        key={`other_${index}`}
        component={systemComponentsGeneralContent(
          otherDetails.component.title,
          otherDetails.component,
          otherContent(otherDetails, selectedSystem)
        )}
        isMobile={layout === 'small'}
        showDialog={showDialog}
        proposalData={proposalData}
        allComponentData={otherDetails.component}
      />
    )
  })
  return results
}

const getUtilityTariffBlocks = (selectedSystem, layout, showDialog, proposalData) => {
  const data = allSystemComponentPlaceholders(selectedSystem)
  return selectedSystem.utility_tariffs.map((utility_tariff, index) => (
    <SystemComponentBlock
      key={`utility_tariff_${index}`}
      component={systemComponentsGeneralContent(
        'Utility Tariff',
        utility_tariff,
        populateGeneric(getContentTemplate(utility_tariff), data)
      )}
      isMobile={layout === 'small'}
      showDialog={showDialog}
      proposalData={proposalData}
      allComponentData={utility_tariff}
    />
  ))
}

const SystemHardwareDisclaimer = ({ selectedSystem }) => {
  if (
    selectedSystem['disclaimer_system_hardware_components'] &&
    selectedSystem['disclaimer_system_hardware_components'].length > 0
  ) {
    return <Disclaimer content={selectedSystem['disclaimer_system_hardware_components']} />
  } else {
    return null
  }
}

const SystemHardware = ({ selectedSystem, showDialog, proposalTemplateSettings, proposalData }) => {
  const translate = useTranslate()
  const existingSystem = useExistingSystem()
  //If no hardward is included, hide the whole section including heading
  const layout = useMediaQuery((theme) => theme.breakpoints.down('md')) ? 'small' : 'large'
  if (
    !proposalTemplateSettings ||
    !proposalTemplateSettings.show_product_types ||
    proposalTemplateSettings.show_product_types.length === 0
  ) {
    return null
  }

  const isRetrofitSystem = existingSystem && existingSystem.uuid !== selectedSystem.uuid
  const showProductTypes = proposalTemplateSettings.show_product_types.split(',')

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ marginBottom: '0px', paddingBottom: '0px' }} className="mye-section-title">
          {proposalTemplateSettings.heading_system_hardware}
        </h1>
        {isRetrofitSystem && (
          <small style={{ color: '#737373', marginTop: '5px' }}>
            {translate('In addition to your existing system')}
          </small>
        )}
      </div>
      <SystemComponentContainer>
        {showProductTypes.map((productType) => {
          if (productType === 'module' && parseInt(selectedSystem.moduleQuantity) > 0) {
            return getModuleBlocks(selectedSystem, layout, showDialog, proposalData)
          } else if (productType === 'inverter' && Object.keys(selectedSystem.inverter_codes_with_details).length > 0) {
            return getInverterBlocks(selectedSystem, layout, showDialog, proposalData)
          } else if (productType === 'battery' && Object.keys(selectedSystem.battery_codes_with_details).length > 0) {
            return getBatteryBlocks(selectedSystem, layout, showDialog, proposalData)
          } else if (productType === 'other' && Object.keys(selectedSystem.other_codes_with_details).length) {
            return getOtherBlocks(selectedSystem, layout, showDialog, proposalData)
          } else if (productType === 'utility_tariff') {
            return getUtilityTariffBlocks(selectedSystem, layout, showDialog, proposalData)
          } else {
            return null
          }
        })}
      </SystemComponentContainer>
      <SystemHardwareDisclaimer selectedSystem={selectedSystem} />
    </div>
  )
}

// Extra buffer required for page-right-scrollbar, borders etc
export default SystemHardware
